import { TextCase } from '../text/TextCase';
import { TimeUnitInfo } from './TimeUnit';
import ArrayUtils from '../collections/ArrayUtils';
import Duration from './Duration';
import FormatError from '../system/FormatError';
import I18NUtils from '../i18n/I18NUtils';
import moment from 'moment';
import StringUtils from '../text/StringUtils';
export default class DateTime {
    constructor(value) {
        if (value instanceof DateTime) {
            this.momentObj = moment(value.momentObj);
        }
        else if (value instanceof Date) {
            this.momentObj = moment(value);
        }
        else if (Number.isNumber(value)) {
            this.momentObj = moment(value);
        }
        else if (Object.isObject(value)) {
            this.momentObj = moment(value);
        }
        else {
            this.momentObj = moment();
        }
    }
    static get monthIndexes() {
        return ArrayUtils.fromRange(0, 11);
    }
    ;
    static get monthNumbers() {
        return ArrayUtils.fromRange(1, 12);
    }
    ;
    static get weekIndexes() {
        return ArrayUtils.fromRange(0, 51);
    }
    ;
    static compare(date1, date2) {
        if (date1 === undefined && date2 === undefined) {
            return 0;
        }
        else if (date1 === undefined && date2 !== undefined) {
            return -1;
        }
        else if (date1 !== undefined && date2 === undefined) {
            return 1;
        }
        const date1Moment = DateTime.isDate(date1) ? moment(date1) : date1.momentObj;
        const date2Moment = DateTime.isDate(date2) ? moment(date2) : date2.momentObj;
        if (date1Moment.isBefore(date2Moment)) {
            return -1;
        }
        else if (date1Moment.isAfter(date2Moment)) {
            return 1;
        }
        else {
            return 0;
        }
    }
    ;
    static equal(date1, date2) {
        if (date1 === undefined && date2 === undefined) {
            return true;
        }
        else if (date1 === undefined && date2 !== undefined) {
            return false;
        }
        else if (date1 !== undefined && date2 === undefined) {
            return false;
        }
        const date1Moment = DateTime.isDate(date1) ? moment(date1) : date1.momentObj;
        const date2Moment = DateTime.isDate(date2) ? moment(date2) : date2.momentObj;
        return date1Moment.isSame(date2Moment);
    }
    ;
    static getMonthName(monthNumber, textCase = TextCase.None) {
        return moment.months(monthNumber - 1).changeCase(textCase);
    }
    ;
    static getMonthRange(startMonth, endMonth) {
        if (startMonth <= endMonth) {
            return ArrayUtils.fromRange(startMonth, endMonth);
        }
        else {
            return ArrayUtils.fromRange(startMonth, 12).concat(ArrayUtils.fromRange(1, endMonth));
        }
    }
    ;
    static getShortMonthName(monthNumber, textCase = TextCase.None) {
        return moment().month(monthNumber - 1).format('MMM').changeCase(textCase);
    }
    ;
    static isDate(obj) {
        return obj instanceof Date;
    }
    ;
    static isDateTime(obj) {
        return obj instanceof DateTime;
    }
    ;
    static isMonthInRange(monthNumber, startMonth, endMonth) {
        if (startMonth === undefined || endMonth === undefined) {
            return false;
        }
        else if (startMonth <= endMonth) {
            return monthNumber >= startMonth && monthNumber <= endMonth;
        }
        else {
            return monthNumber >= endMonth && monthNumber <= startMonth;
        }
    }
    ;
    static parse(value) {
        const result = DateTime.tryParse(value);
        if (result === undefined) {
            throw new FormatError(`'${value}' does not represent a Date in a recognized RFC2822 or ISO format.`);
        }
        return result;
    }
    ;
    static tryParse(value, valueOnFailure) {
        const result = I18NUtils.defaultDateParser.tryParse(value, valueOnFailure);
        if (result) {
            return new DateTime(result);
        }
        else {
            return undefined;
        }
    }
    ;
    get day() {
        return this.momentObj.day();
    }
    ;
    set day(value) {
        this.momentObj.day(value);
    }
    ;
    get daysInMonth() {
        return this.momentObj.daysInMonth();
    }
    ;
    get hour() {
        return this.momentObj.hour();
    }
    ;
    set hour(value) {
        this.momentObj.hour(value);
    }
    ;
    get millisecond() {
        return this.momentObj.millisecond();
    }
    ;
    set millisecond(value) {
        this.momentObj.millisecond(value);
    }
    ;
    get minute() {
        return this.momentObj.minute();
    }
    ;
    set minute(value) {
        this.momentObj.minute(value);
    }
    ;
    get month() {
        return this.momentObj.month();
    }
    ;
    set month(value) {
        this.momentObj.month(value);
    }
    ;
    get second() {
        return this.momentObj.second();
    }
    ;
    set second(value) {
        this.momentObj.second(value);
    }
    ;
    get utcOffsetMinutes() {
        return this.momentObj.utcOffset();
    }
    ;
    set utcOffsetMinutes(value) {
        this.momentObj.utcOffset(value);
    }
    ;
    get weekday() {
        return this.momentObj.weekday();
    }
    ;
    set weekday(value) {
        this.momentObj.weekday(value);
    }
    ;
    get year() {
        return this.momentObj.year();
    }
    ;
    set year(value) {
        this.momentObj.year(value);
    }
    ;
    add(duration) {
        return new DateTime(this.momentObj.add(duration.totalMilliseconds, 'milliseconds'));
    }
    ;
    addDays(days) {
        return new DateTime(this.momentObj.add(days, 'days'));
    }
    ;
    addHouse(hours) {
        return new DateTime(this.momentObj.add(hours, 'hours'));
    }
    ;
    addMilliseconds(milliseconds) {
        return new DateTime(this.momentObj.add(milliseconds, 'milliseconds'));
    }
    ;
    addMinutes(minutes) {
        return new DateTime(this.momentObj.add(minutes, 'minutes'));
    }
    ;
    addMonths(months) {
        return new DateTime(this.momentObj.add(months, 'months'));
    }
    ;
    addSeconds(seconds) {
        return new DateTime(this.momentObj.add(seconds, 'seconds'));
    }
    ;
    addWeeks(weeks) {
        return new DateTime(this.momentObj.add(weeks, 'weeks'));
    }
    ;
    addYears(years) {
        return new DateTime(this.momentObj.add(years, 'years'));
    }
    ;
    clone() {
        return new DateTime(this.momentObj);
    }
    ;
    diff(other) {
        return Duration.fromRange(other.toDate(), this.toDate());
    }
    ;
    equals(other) {
        return DateTime.equal(this, other);
    }
    ;
    format(format) {
        return this.momentObj.format(format);
    }
    ;
    isGreaterThan(other) {
        return DateTime.compare(this, other) === 1;
    }
    ;
    isGreaterThanOrEqualTo(other) {
        return DateTime.compare(this, other) >= 1;
    }
    ;
    isLessThan(other) {
        return DateTime.compare(this, other) === -1;
    }
    ;
    isLessThanOrEqualTo(other) {
        return DateTime.compare(this, other) <= -1;
    }
    ;
    isLocal() {
        return this.momentObj.isLocal();
    }
    ;
    isUTC() {
        return this.momentObj.isUTC();
    }
    ;
    startOf(timeUnit) {
        return new DateTime(this.momentObj.clone().startOf(TimeUnitInfo.toMomentStartOf(timeUnit)));
    }
    ;
    subtract(duration) {
        return new DateTime(this.momentObj.clone().subtract(duration.totalMilliseconds, 'milliseconds'));
    }
    ;
    toDate() {
        return this.momentObj.toDate();
    }
    toLocal() {
        return new DateTime(this.momentObj.clone().local());
    }
    ;
    toLongDate() {
        return this.momentObj.format('LL');
    }
    ;
    toLongDateTime() {
        return this.toLongDate() + this.toLongTime();
    }
    ;
    toLongTime() {
        return this.momentObj.format('LTS');
    }
    ;
    toLongYearlessDate() {
        return StringUtils.matchAndReplaceAll(this.momentObj.format('LL'), ',?\\s*[0-9]{4}\\s*', '');
    }
    ;
    toShortDate() {
        return this.momentObj.format('L');
    }
    ;
    toShortDateTime() {
        return this.toShortDate() + ' ' + this.toShortTime();
    }
    ;
    toShortTime() {
        return this.momentObj.format('LT');
    }
    ;
    toUnixTimestamp() {
        return this.momentObj.unix();
    }
    ;
    toUTC() {
        return new DateTime(this.momentObj.clone().utc());
    }
    ;
    truncateDays() {
        return new DateTime({ year: this.momentObj.year(), month: this.momentObj.month() });
    }
    ;
    truncateHours() {
        return new DateTime({ year: this.momentObj.year(), month: this.momentObj.month(), day: this.momentObj.day() });
    }
    ;
    truncateMilliseconds() {
        return new DateTime({ year: this.momentObj.year(), month: this.momentObj.month(), day: this.momentObj.day(), hour: this.momentObj.hours(), minute: this.momentObj.minutes(), second: this.momentObj.seconds() });
    }
    ;
    truncateMinutes() {
        return new DateTime({ year: this.momentObj.year(), month: this.momentObj.month(), day: this.momentObj.day(), hour: this.momentObj.hours() });
    }
    ;
    truncateMonths() {
        return new DateTime({ year: this.momentObj.year() });
    }
    ;
    truncateSeconds() {
        return new DateTime({ year: this.momentObj.year(), month: this.momentObj.month(), day: this.momentObj.day(), hour: this.momentObj.hours(), minute: this.momentObj.minutes() });
    }
    ;
    withDay(value) {
        return new DateTime(this.momentObj.clone().day(value));
    }
    ;
    withHour(value) {
        return new DateTime(this.momentObj.clone().hour(value));
    }
    ;
    withMillisecond(value) {
        return new DateTime(this.momentObj.clone().millisecond(value));
    }
    ;
    withMinute(value) {
        return new DateTime(this.momentObj.clone().minute(value));
    }
    ;
    withMonth(value) {
        return new DateTime(this.momentObj.clone().month(value));
    }
    ;
    withSecond(value) {
        return new DateTime(this.momentObj.clone().second(value));
    }
    ;
    withUTCOffset(value) {
        return new DateTime(this.momentObj.clone().utcOffset(value));
    }
    ;
    withWeekday(value) {
        return new DateTime(this.momentObj.clone().weekday(value));
    }
    ;
    withYear(value) {
        return new DateTime(this.momentObj.clone().year(value));
    }
    ;
}
DateTime.DAYS_PER_MONTH = 30.436875;
DateTime.DAYS_PER_WEEK = 7;
DateTime.DAYS_PER_YEAR = 365.24225;
DateTime.MINUTES_PER_HOUR = 60;
DateTime.MONTHS_PER_YEAR = 12;
DateTime.WEEKS_PER_YEAR = 52.1429;
//# sourceMappingURL=DateTime.js.map